import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import { t } from "ember-intl";
import { and, or } from "ember-truth-helpers";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import GButtonIcon from "glesys-controlpanel/components/g-button/icon";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import EmptyState from "glesys-controlpanel/components/empty-state";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityList from "glesys-controlpanel/components/entity-list";
import CostPlain from "glesys-controlpanel/components/cost/plain";
import { array } from "@ember/helper";
import IconsSidebarStorage from "glesys-controlpanel/components/icons/sidebar/storage";

export default class ArchivesOverview extends Component {
  @service store;

  get project() {
    return this.args.model.project;
  }

  get archiveArguments() {
    return this.args.model.archiveArguments;
  }

  get archives() {
    return this.args.model.archives;
  }

  get archivesFiltered() {
    return this.archives.filter((archive) => !archive.isNew);
  }

  createArchiveTask = task(async (username, password, size) => {
    let project = this.project;

    let archive = this.store.createRecord("archive", {
      project,
      username,
      password,
      size,
    });

    await this.store.request(saveRecord(archive)).catch((error) => {
      archive.unloadRecord();
      throw error;
    });
  });

  <template>
    <div class="bg-gray-50">
      <PageContent>
        <PageContentServiceIntro @title={{t "archives.page-title"}}>
          {{#if (and (canManage) this.archivesFiltered)}}
            <GButtonIcon
              data-test-create-archive-button
              @display="default"
              @color="green"
              {{on
                "click"
                (modal
                  "modals/archives-create"
                  project=this.project
                  sizes=this.archiveArguments.size
                  task=this.createArchiveTask
                )
              }}
            >
              <IconsPlus class="w-4 -ml-1 mr-1" />
              {{t "button.create"}}
            </GButtonIcon>
          {{/if}}
        </PageContentServiceIntro>

        <EntityList @items={{this.archivesFiltered}}>
          <:default as |archive|>
            <Entity
              @route="archive"
              @model={{archive.id}}
              @title={{or archive.description archive.username}}
              @key={{if archive.description archive.username}}
              @linkLabel={{t "archives.manage-archive-aria-label" name=archive.username}}
            >
              <:fields>
                <EntityField data-test-archive-usage @label={{t "label.usage"}}>
                  {{archive.usage.usage}}/{{archive.usage.max}}
                  {{archive.usage.unit}}
                </EntityField>
                <EntityField data-test-archive-cost @label={{t "label.cost"}}>
                  <CostPlain @currency={{archive.cost.currency}} @price={{archive.cost.amount}} />
                </EntityField>
              </:fields>
            </Entity>
          </:default>
          <:empty>
            <EmptyState
              @icon={{IconsSidebarStorage}}
              @title={{t "archives.empty-state.title"}}
              @description={{t "archives.empty-state.description"}}
              @points={{array
                (t "archives.empty-state.usp-list.access-from-anywhere")
                (t "archives.empty-state.usp-list.shared-drive")
                (t "archives.empty-state.usp-list.long-term")
              }}
              @learnMoreUrl="https://docs.glesys.com/products/storage/archive-storage"
              @learnMoreAriaLabel={{t "archives.empty-state.learn-more-aria-label"}}
              @createLabel={{t "archives.empty-state.button"}}
              @onCreate={{if
                (canManage)
                (modal
                  "modals/archives-create"
                  project=this.project
                  sizes=this.archiveArguments.size
                  task=this.createArchiveTask
                )
              }}
            />
          </:empty>
        </EntityList>
      </PageContent>
    </div>
  </template>
}
